import { useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSocket } from '@/contexts/SocketContext';
import { useAuth } from '@/core/auth';
import { apiClient } from '@/core/api/client';

// Query key for React Query deduplication
const SMS_UNREAD_COUNT_KEY = ['sms', 'unread', 'count'];

interface SMSUnreadResponse {
  success: boolean;
  data: {
    unreadCount: number;
  };
}

/**
 * Hook to fetch and manage unread SMS count
 * Uses React Query for automatic request deduplication
 * Listens to WebSocket events for real-time updates
 */
export const useSMSUnreadCount = () => {
  const { socket, isConnected } = useSocket();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Only enable query when authenticated and user has admin/bureau role
  const canAccessSMS = Boolean(isAuthenticated && user && (user.role === 'admin' || user.role === 'bureau'));

  // Fetch unread count using React Query
  const {
    data: unreadData,
    isLoading,
    refetch: refetchQuery
  } = useQuery({
    queryKey: SMS_UNREAD_COUNT_KEY,
    queryFn: async (): Promise<number> => {
      try {
        const response = await apiClient.get<SMSUnreadResponse>('/api/sms-gateway/admin/unread-count');
        return response.success ? response.data.unreadCount : 0;
      } catch (error) {
        console.error('Failed to fetch SMS unread count:', error);
        return 0;
      }
    },
    enabled: canAccessSMS, // Only run query when authenticated
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false, // Handled by WebSocket
  });

  // Debounced refetch function
  const debouncedRefetch = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: SMS_UNREAD_COUNT_KEY });
    }, 300);
  }, [queryClient]);

  // Public refetch function
  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: SMS_UNREAD_COUNT_KEY });
  }, [queryClient]);

  // WebSocket listeners for real-time updates
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleSMSReceived = () => debouncedRefetch();
    const handleSMSSent = () => debouncedRefetch();
    const handleSMSProcessed = () => debouncedRefetch();

    // Listen for SMS-specific events
    socket.on('sms:received', handleSMSReceived);
    socket.on('sms:sent', handleSMSSent);
    socket.on('sms:processed', handleSMSProcessed);

    return () => {
      socket.off('sms:received', handleSMSReceived);
      socket.off('sms:sent', handleSMSSent);
      socket.off('sms:processed', handleSMSProcessed);

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [socket, isConnected, debouncedRefetch]);

  // Fallback polling when WebSocket is disconnected (only when authenticated)
  useEffect(() => {
    if (!canAccessSMS) {
      // Clear any existing polling when not authenticated
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    if (!isConnected) {
      pollingIntervalRef.current = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: SMS_UNREAD_COUNT_KEY });
      }, 30000);
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isConnected, queryClient, canAccessSMS]);

  return {
    unreadCount: unreadData ?? 0,
    isLoading,
    refetch
  };
};

export default useSMSUnreadCount;
